import type {
  WorkerRequest,
  WorkerResponse,
  WorkerCommand,
  DashboardQueryFunction,
  DashboardQueryRequest,
  CalendarCacheRequest,
  ACLDataRequest,
  GenericCacheRequest,
  ClientBookingsRequest,
  ContractFilterRequest,
  WidgetEventCountRequest,
  EventsByIdsRequest,
  EventsByIdsResult,
  SeriesEventCountsRequest,
  SeriesEventCountsResult,
  SeriesEventsRequest,
  SeriesEventsResult,
  TrainingOverviewClientDataRequest,
  TrainingOverviewClientDataResult,
  BatchTrainingOverviewRequest,
  BatchTrainingOverviewResult
} from '../worker/types/WorkerMessages'

import { getQueryWorker, serverWorker } from '../worker/workerInstances'
import { useActionListStore } from '@/stores/actionList'

interface PendingRequest {
  resolve: (value: any) => void
  reject: (reason: any) => void
  timeout: NodeJS.Timeout
}

/**
 * Eine live eingetroffene Zeile aus `calendar_events`/`calendar_bookings` —
 * egal ob per Socket-Push (kann partiell sein) oder HTTP-Inkrementell-Sync
 * (vollständige Serverzeile). Beide Pfade schreiben nach IndexedDB; Ansichten,
 * die Termine gezielt über den Worker geladen haben (angefragte Trainings einer
 * Website-Anfrage), erfahren davon sonst nichts.
 */
export interface CalendarLiveChange {
  table: 'calendar_events' | 'calendar_bookings'
  row: any
}

class WorkerService {
  private static pendingRequests = new Map<string, PendingRequest>()
  private static initialized = false
  private static queryWorkerReady = false
  private static calendarChangeListeners = new Set<(changes: CalendarLiveChange[]) => void>()

  /** Auf live eintreffende Termin-/Buchungsänderungen hören (Push und Sync). */
  public static onCalendarChanges(listener: (changes: CalendarLiveChange[]) => void): void {
    this.calendarChangeListeners.add(listener)
  }

  public static offCalendarChanges(listener: (changes: CalendarLiveChange[]) => void): void {
    this.calendarChangeListeners.delete(listener)
  }

  private static emitCalendarChanges(changes: CalendarLiveChange[]): void {
    if (!changes.length) return
    for (const listener of this.calendarChangeListeners) {
      try {
        listener(changes)
      } catch (err) {
        console.warn('[WorkerService] calendar change listener failed:', err)
      }
    }
  }

  /**
   * Initialize the WorkerService
   */
  public static init(): void {
    if (this.initialized) {
      console.log('[WorkerService] Already initialized — skipping duplicate init')
      return
    }
    this.initialized = true
    console.log('[WorkerService] Initializing')
    this.initQueryWorker()
    this.initServerWorker()
  }

  /**
   * Initialize Query Worker with message handler
   */
  private static initQueryWorker(): void {
    console.log('[WorkerService] Initializing query worker')

    getQueryWorker().addEventListener('message', (event) => {
      const response = event.data as WorkerResponse

      // Handle init success
      if (response.cmd === 'init_success') {
        console.log('[WorkerService] Query worker initialized')
        this.queryWorkerReady = true
        return
      }

      // Handle query responses
      this.handleWorkerResponse(response)
    })

    getQueryWorker().addEventListener('error', (error) => {
      console.error('[WorkerService] Query worker error:', error)
    })

    // Send init directly — no pending promise needed; init_success is handled
    // by the event listener above (early return path), not handleWorkerResponse
    getQueryWorker().postMessage({ id: crypto.randomUUID(), cmd: 'init' })
  }

  /**
   * Initialize Server Worker (legacy)
   */
  private static initServerWorker(): void {
    console.log('[WorkerService] Initializing server worker')

    serverWorker.addEventListener('message', (event) => {
      switch (event.data.cmd) {
        case 'initsuccess':
          console.log('[WorkerService] Server worker initialized')
          break
        case 'pushProxy': {
          // Termin-/Buchungsänderungen anderer Geräte an interessierte
          // Ansichten weiterreichen (Store-Caches versorgt tables.ts selbst)
          const push = event.data.data
          if (
            (push?.cmd === 'new' || push?.cmd === 'update') && push.result &&
            (push.table === 'calendar_events' || push.table === 'calendar_bookings')
          ) {
            WorkerService.emitCalendarChanges([{ table: push.table, row: push.result }])
          }
          break
        }
        case 'socketconnection':
          console.log('[WorkerService] Socket connection:', event.data)
          break
        case 'loading':
          if (event.data.payload?.status === 'done') {
            console.log('[WorkerService] Server worker loading done — loading action list')
            try {
              const actionStore = useActionListStore()
              actionStore.loadFromIDB()
              actionStore.warmupClassifier()
            } catch (err) {
              console.warn('[WorkerService] Failed to load action list:', err)
            }
          }
          break
        case 'syncChanges': {
          // Diff of changed records from tracked tables — feed into action rules engine
          const { changes } = event.data
          if (changes?.length) {
            try {
              useActionListStore().processDiff(changes)
            } catch (err) {
              console.warn('[WorkerService] processDiff error:', err)
            }
            WorkerService.emitCalendarChanges(
              changes
                .filter((c: any) =>
                  (c.table === 'calendar_events' || c.table === 'calendar_bookings') && c.after)
                .map((c: any) => ({ table: c.table, row: c.after }))
            )
          }
          break
        }
      }
    })
  }

  /**
   * Handle worker response
   */
  private static handleWorkerResponse(response: WorkerResponse): void {
    // Silently ignore legacy messages (tableupdate, eventscache, bookingscache, etc.)
    // These are handled by the tables.ts listener and have no `id` / `success` field.
    if (!response.id || response.success === undefined) {
      return
    }

    const pending = this.pendingRequests.get(response.id)

    if (!pending) {
      // Ignore init_success and other one-off responses that aren't pending requests
      return
    }

    // Clear timeout
    clearTimeout(pending.timeout)

    // Resolve or reject based on success
    if (response.success) {
      pending.resolve(response.data)
    } else {
      const error = new Error(response.error?.message || 'Worker query failed')
      pending.reject(error)
    }

    // Clean up
    this.pendingRequests.delete(response.id)
  }

  /**
   * Send message to query worker and return Promise
   */
  private static sendQueryWorkerMessage<T>(request: WorkerRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(request.id)
        reject(new Error(`Query timeout after 120s: ${request.cmd}`))
      }, 120000)

      this.pendingRequests.set(request.id, { resolve, reject, timeout })
      getQueryWorker().postMessage(request)
    })
  }

  /**
   * Execute a dashboard query
   */
  public static async dashboardQuery(
    fn: DashboardQueryFunction,
    // Ohne `function`: die Abfragefunktion kommt als eigener Parameter herein
    // und wird unten separat in die Nutzlast gelegt. Der Typ verlangte sie
    // trotzdem im config-Objekt — eine Anforderung, die kein Aufrufer erfüllt
    // (und erfüllen soll).
    config: Omit<DashboardQueryRequest, 'function'>
  ): Promise<any> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'dashboard_query' as WorkerCommand,
      payload: { fn, config }
    }

    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Get calendar cache (events + bookings)
   */
  public static async getCalendarCache(
    payload: CalendarCacheRequest
  ): Promise<any> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'calendar_cache' as WorkerCommand,
      payload
    }

    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Get ACL data for a user
   */
  public static async getACLData(
    payload: ACLDataRequest
  ): Promise<any> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'acl_data' as WorkerCommand,
      payload
    }

    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Get generic cache (all tables)
   */
  public static async getGenericCache(
    payload?: GenericCacheRequest
  ): Promise<any> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'generic_cache' as WorkerCommand,
      payload
    }

    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Get client bookings
   */
  public static async getClientBookings(
    payload: ClientBookingsRequest
  ): Promise<any> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'client_bookings' as WorkerCommand,
      payload
    }

    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Filter contracts by service
   */
  public static async filterContracts(
    payload: ContractFilterRequest
  ): Promise<any> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'contract_filter' as WorkerCommand,
      payload
    }

    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Get widget event count from IDB (any date range, not limited to month cache)
   */
  public static async getWidgetEventCount(
    payload: WidgetEventCountRequest
  ): Promise<{ count: number }> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'widget_event_count' as WorkerCommand,
      payload
    }
    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Termine samt Buchungen gezielt nach ID aus IndexedDB holen.
   *
   * `calendar_events`/`calendar_bookings` liegen nicht im Store — dort steht
   * nur das geladene Monatsfenster. Für Termine außerhalb davon (z. B. die
   * angefragten Trainings einer Website-Anfrage) führt der Weg über den Worker.
   */
  public static async getEventsByIds(
    payload: EventsByIdsRequest
  ): Promise<EventsByIdsResult> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'events_by_ids' as WorkerCommand,
      payload
    }
    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Termine je Serie zaehlen (alle Serien der Company in einem IDB-Durchlauf).
   *
   * `calendar_events` steht im Store nur als leeres Array — die Serienuebersicht
   * muss die Zahlen hier holen. Ein Aufruf liefert die Map fuer alle Serien;
   * pro Serie einzeln anzufragen waere ein Tabellenscan je Serie.
   */
  public static async getSeriesEventCounts(
    payload: SeriesEventCountsRequest
  ): Promise<SeriesEventCountsResult> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'series_event_counts' as WorkerCommand,
      payload
    }
    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Die Termine einer Serie samt Teilnehmerzahl je Termin — auf Abruf, wenn
   * eine Serie in der Uebersicht aufgeklappt wird.
   */
  public static async getSeriesEvents(
    payload: SeriesEventsRequest
  ): Promise<SeriesEventsResult> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'series_events' as WorkerCommand,
      payload
    }
    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Get training overview data for a client (future series events + 30-day count)
   * Queries IndexedDB directly via the worker — reliable for full series history
   */
  public static async getTrainingOverviewClientData(
    payload: TrainingOverviewClientDataRequest
  ): Promise<TrainingOverviewClientDataResult> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'training_overview_client_data' as WorkerCommand,
      payload
    }
    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Batch fetch of event bookings + per-attendee overview data in one IDB pass.
   * Much faster than N individual getTrainingOverviewClientData calls.
   * Also returns the full booking rows so future-month events can be displayed
   * without relying on Calendar_bookings_month_cache.
   */
  public static async getBatchTrainingOverviewData(
    payload: BatchTrainingOverviewRequest
  ): Promise<BatchTrainingOverviewResult> {
    const request: WorkerRequest = {
      id: crypto.randomUUID(),
      cmd: 'batch_training_overview' as WorkerCommand,
      payload
    }
    return this.sendQueryWorkerMessage(request)
  }

  /**
   * Legacy: Direct message send (for compatibility)
   */
  public static QueryWorkerSendMessage(query: any): void {
    console.warn('[WorkerService] QueryWorkerSendMessage is deprecated. Use typed methods instead.')
    getQueryWorker().postMessage(query)
  }

  /**
   * Server worker message send (legacy)
   */
  public static ServerWorkerSendMessage(query: any): void {
    serverWorker.postMessage(query)
  }

  /**
   * Check if worker is ready
   */
  public static isReady(): boolean {
    return this.queryWorkerReady
  }
}

export default WorkerService
